import { FC, ReactElement, useState } from 'react'
import { useClientConnectedUser } from '@stream-io/feeds-client/react-bindings'

import { Button } from '@/components'
import { useChatConnection } from '@/context/ChatConnectionContext'
import { useFeedConnection } from '@/context/FeedConnectionContext'
import { createLocalStorageItem, LocalStorageItem, parseBoolean } from '@/helpers/localStorageItem'
import { deriveNotificationCategoryLevels, saveNotificationCategoryLevels } from '@/helpers/notificationCategoryLevels'
import { trans } from '@/helpers/trans'
import { useLocalStorageItem } from '@/hooks/useLocalStorageItem'
import { usePushRegistration } from '@/hooks/usePushRegistration'
import { NotificationCategory, NotificationCategoryLevels, NotificationLevel } from '@/types'

import './EnableNotificationsBanner.scss'

type EnableNotificationsBannerContext = 'messages' | 'newsfeed' | 'notificationSettings'

interface EnableNotificationsBannerProps {
    context?: EnableNotificationsBannerContext
    className?: string
}

// The category a page's CTA is about. The notification settings page has none: there the CTA is
// only about the OS notification permission, which the settings on that page depend on.
const CONTEXT_CATEGORY: Record<EnableNotificationsBannerContext, NotificationCategory | undefined> = {
    messages: NotificationCategory.chatMessages,
    newsfeed: NotificationCategory.repliesToComments,
    notificationSettings: undefined,
}

const dismissedStorage = (context: EnableNotificationsBannerContext): LocalStorageItem<boolean> => createLocalStorageItem({
    key: `valk-talk-enable-notifications-cta-dismissed:${context}`,
    fallback: false,
    parse: parseBoolean,
})

export const EnableNotificationsBanner: FC<EnableNotificationsBannerProps> = ({
    context = 'messages',
    className = '',
}): ReactElement | null => {
    const { client: chatClient, userId } = useChatConnection()
    const { client: feedsClient } = useFeedConnection()
    const connectedFeedsUser = useClientConnectedUser()
    const {
        bridgeAvailable,
        status,
        permissionLoading,
        enabled: globalEnabled,
        loading,
        enable,
    } = usePushRegistration()
    const dismissible = context !== 'notificationSettings'
    const [isDismissed, setIsDismissed] = useLocalStorageItem(dismissedStorage(context))
    const dismissed = dismissible && isDismissed

    const [overrideLevels, setOverrideLevels] = useState<NotificationCategoryLevels | null>(null)
    const levels = overrideLevels ?? deriveNotificationCategoryLevels(chatClient, connectedFeedsUser, userId)

    const category = CONTEXT_CATEGORY[context]
    const categoryEnabled = !! category && levels?.[category] === NotificationLevel.pushAndInApp

    const handleDismiss = (): void => {
        setIsDismissed(true)
    }

    const enableCategory = async (): Promise<void> => {
        if (! category || ! chatClient || ! feedsClient) {
            return
        }

        // Re-derive rather than using `levels`: enable() may have just applied the default levels.
        const currentLevels = deriveNotificationCategoryLevels(chatClient, connectedFeedsUser, userId)

        if (! currentLevels || currentLevels[category] === NotificationLevel.pushAndInApp) {
            setOverrideLevels(currentLevels)
            return
        }

        const nextLevels = { ...currentLevels, [category]: NotificationLevel.pushAndInApp }

        setOverrideLevels(nextLevels)
        await saveNotificationCategoryLevels(chatClient, feedsClient, userId, nextLevels)
    }

    const handleEnable = async (): Promise<void> => {
        try {
            const registered = globalEnabled || await enable()

            if (registered) {
                await enableCategory()
            }
        } catch (error) {
            console.error(error)
        }
    }

    const isDenied = status === 'denied'

    if (! bridgeAvailable || dismissed || permissionLoading) {
        return null
    }

    if (! category && status === 'granted') {
        return null
    }

    // An explicit rejection can only be undone in the OS settings, so outside the settings page
    // the CTA would just nag; there the settings are unusable without it, so it stays.
    if (category && (isDenied || loading || ! levels || (globalEnabled && categoryEnabled))) {
        return null
    }

    const keyPrefix = `pages.${context}`

    return (
        <div className={`enable-notifications-banner notice notice--info ${className}`}>
            <p className="enable-notifications-banner__title">
                {trans(`${keyPrefix}.enableNotificationsCtaTitle`)}
            </p>
            <p className="enable-notifications-banner__body">
                {trans(`${keyPrefix}.${isDenied ? 'enableNotificationsCtaDeniedBody' : 'enableNotificationsCtaBody'}`)}
            </p>
            <div className="enable-notifications-banner__actions">
                {! isDenied && (
                    <Button
                        type="button"
                        text={trans(`${keyPrefix}.enableNotificationsCtaAction`)}
                        onClick={handleEnable}
                        className="enable-notifications-banner__action"
                    />
                )}
                {dismissible && (
                    <Button
                        type="button"
                        text={trans(`${keyPrefix}.enableNotificationsCtaDismiss`)}
                        onClick={handleDismiss}
                        className="enable-notifications-banner__dismiss"
                    />
                )}
            </div>
        </div>
    )
}
